// Normalized Kinmantle models shared by the Worker API and the React app.
// Providers translate their raw responses into these shapes; widgets only
// ever see these.

import { DEFAULT_MOUNTAINS, type MountainSettings } from "./mountains";
import { DEFAULT_SCREENS, type ScreenSettings } from "./screens";
import { DEFAULT_NEWS, type NewsSettings } from "./news";
import type { SeasonalChoice } from "./themes";
import type { LookoutSettings } from "./webcams";

/** Data freshness state every widget can display. */
export type DataState =
  | "loading"
  | "live"
  | "stale"
  | "offline"
  | "unavailable"
  | "permissionRequired"
  | "disabled";

export interface ApiError {
  error: string;
  code: string;
}

// ---------- Household ----------

export type MemberRole = "owner" | "adult" | "member";

export interface Member {
  id: string;
  displayName: string;
  color: string;
  email: string | null;
  role: MemberRole;
  canSignIn: boolean;
  /** Children get chores/routines and can't change settings. */
  isChild: boolean;
  sortOrder: number;
}

export interface Me {
  member: Member;
  household: { id: string; name: string; timezone: string };
  isAdmin: boolean;
  devBypass: boolean;
  /** The sample household shown by "Try the demo", not a real one. */
  demo: boolean;
}

export interface HomeLocation {
  lat: number;
  lon: number;
  /** Human label, e.g. "Denver, CO". */
  label: string;
}

export type TemperatureUnit = "F" | "C";
export type ThemeChoice = "auto" | "light" | "dark";
export type RewardCurrency = "stars" | "dollars";

/** Which screen (layout) the wall shows in each part of the day. */
export interface ScreenSchedule {
  morning: { start: string; layoutKey: string };
  day: { start: string; layoutKey: string };
  night: { start: string; layoutKey: string };
}

/** When the household is asleep: wall displays go dark after a few untouched minutes (src/app/Sleep.tsx). */
export interface BedtimeSettings {
  start: string;
  end: string;
}

export interface TickerSettings {
  news: boolean;
  /** RSS/Atom feed URL for headlines (must be on the allow-list in shared/feeds.ts). */
  newsFeed: string;
  onThisDay: boolean;
  quote: boolean;
  joke: boolean;
  word: boolean;
  fact: boolean;
}

export interface HouseholdSettings {
  location: HomeLocation | null;
  temperatureUnit: TemperatureUnit;
  clock24h: boolean;
  theme: ThemeChoice;
  /** Seasonal look over the theme (shared/themes.ts): "auto" follows each theme's dates. */
  seasonalTheme: SeasonalChoice;
  defaultLayoutId: string | null;
  /** Observations older than this are not treated as current. */
  observationMaxAgeMinutes: number;
  /** Optional manually pinned NWS station id (e.g. "KDEN"). */
  pinnedStationId: string | null;
  rewardCurrency: RewardCurrency;
  schedule: ScreenSchedule;
  /** Minutes without a touch before a wall display returns to the scheduled screen; 0 = never. */
  idleReturnMinutes: number;
  bedtime: BedtimeSettings;
  ticker: TickerSettings;
  /** Wall displays switch to the Alerts screen for life-threatening weather alerts. */
  alertTakeover: boolean;
  /** Places for the Outdoors screen's mountain forecasts (picked on the map). */
  outdoorSpots: OutdoorSpot[];
  /** USGS stream gauges to show; empty = the nearest few to home. */
  riverSites: string[];
  /** What the household gets outside for; themes the Outdoors screen. */
  outdoorInterests: OutdoorInterest[];
  /** Ski mountains on the Outdoors screen and the powder-day alert (shared/mountains.ts). */
  mountains: MountainSettings;
  /** The News screen: sources, the hero, teams (shared/news.ts). */
  news: NewsSettings;
  /** Lookout's camera groups switched on or off by hand (shared/webcams.ts). */
  lookout: LookoutSettings;
  /** Screens turned off: gone from navigation, the schedule and background work (shared/screens.ts). */
  screens: ScreenSettings;
}

export type OutdoorInterest = "biking" | "hiking" | "hunting" | "fishing" | "paddling" | "camping" | "skiing";

export interface OutdoorSpot {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

export const DEFAULT_SETTINGS: HouseholdSettings = {
  location: null,
  temperatureUnit: "F",
  clock24h: false,
  // Dark suits a wall display best; "auto" follows the real sunrise/sunset.
  theme: "dark",
  seasonalTheme: "auto",
  defaultLayoutId: null,
  observationMaxAgeMinutes: 90,
  pinnedStationId: null,
  rewardCurrency: "stars",
  schedule: {
    morning: { start: "06:00", layoutKey: "morning" },
    day: { start: "09:00", layoutKey: "family-home" },
    night: { start: "20:30", layoutKey: "night" },
  },
  idleReturnMinutes: 5,
  bedtime: { start: "22:00", end: "06:00" },
  ticker: { news: true, newsFeed: "https://feeds.npr.org/1001/rss.xml", onThisDay: true, quote: true, joke: true, word: true, fact: true },
  alertTakeover: true,
  outdoorSpots: [],
  riverSites: [],
  outdoorInterests: ["hiking", "biking"],
  mountains: DEFAULT_MOUNTAINS,
  news: DEFAULT_NEWS,
  lookout: { groups: {} },
  screens: DEFAULT_SCREENS,
};

/**
 * Settings with every field present: stored or saved values over the
 * defaults, and object settings merged one level so fields added later get
 * their defaults. A phone's saved copy can predate a feature by weeks.
 */
export function withSettingDefaults(raw: Partial<Record<keyof HouseholdSettings, unknown>> | null | undefined): HouseholdSettings {
  const out = { ...DEFAULT_SETTINGS } as Record<string, unknown>;
  if (!raw || typeof raw !== "object") return out as unknown as HouseholdSettings;
  for (const [key, value] of Object.entries(raw)) {
    if (!(key in out) || value === undefined) continue;
    const def = out[key];
    out[key] = def && typeof def === "object" && !Array.isArray(def) && value && typeof value === "object" && !Array.isArray(value) ? { ...def, ...value } : value;
  }
  return out as unknown as HouseholdSettings;
}

// ---------- Weather ----------

export interface StationCandidate {
  id: string;
  name: string;
  distanceKm: number;
  /** Why this station was not selected; absent for the selected one. */
  rejected?: string;
}

export interface Observation {
  station: { id: string; name: string; distanceKm: number; lat: number; lon: number };
  observedAt: string;
  description: string | null;
  icon: WeatherIcon;
  isDaytime: boolean;
  temperatureC: number;
  dewpointC: number | null;
  feelsLikeC: number | null;
  humidityPct: number | null;
  windSpeedKph: number | null;
  windGustKph: number | null;
  windDirectionDeg: number | null;
  pressurePa: number | null;
  visibilityM: number | null;
}

export interface ForecastPeriod {
  name: string;
  startTime: string;
  endTime: string;
  isDaytime: boolean;
  temperature: number;
  temperatureUnit: TemperatureUnit;
  shortForecast: string;
  detailedForecast: string;
  precipChancePct: number | null;
  windSpeed: string;
  windDirection: string;
  icon: WeatherIcon;
}

export interface HourlyPeriod {
  startTime: string;
  temperature: number;
  temperatureUnit: TemperatureUnit;
  shortForecast: string;
  precipChancePct: number | null;
  icon: WeatherIcon;
  isDaytime: boolean;
}

export interface WeatherAlert {
  id: string;
  event: string;
  headline: string | null;
  severity: string;
  urgency: string;
  effective: string | null;
  ends: string | null;
  senderName: string | null;
  description: string;
  instruction: string | null;
  /** Counties/zones covered, as NWS words it. */
  areaDesc?: string | null;
}

/** Active alerts for home, polled often by wall displays for the automatic Alerts screen. */
export interface AlertsPayload {
  alerts: WeatherAlert[];
  fetchedAt: string;
  servedFromCache: boolean;
}

/** Storm-based warning outlines for the radar map (lat/lon rings). */
export interface WarningShape {
  id: string;
  event: string;
  ends: string | null;
  rings: [number, number][][];
}

export type WeatherIcon =
  | "clear"
  | "partly-cloudy"
  | "cloudy"
  | "fog"
  | "rain"
  | "showers"
  | "thunder"
  | "snow"
  | "sleet"
  | "wind"
  | "haze"
  | "hot"
  | "cold"
  | "unknown";

export interface SourceStamp {
  source: string;
  fetchedAt: string;
  /** True when the Worker could not reach the source and served last-known data. */
  servedFromCache: boolean;
}

export interface WeatherSummary {
  location: HomeLocation & { gridId: string; timeZone: string };
  observation: Observation | null;
  observationNote: string | null;
  stationCandidates: StationCandidate[];
  daily: ForecastPeriod[];
  hourly: HourlyPeriod[];
  alerts: WeatherAlert[];
  stamps: {
    observation: SourceStamp | null;
    forecast: SourceStamp | null;
    alerts: SourceStamp | null;
  };
}

export interface RadarTimeline {
  provider: "noaa-mrms";
  wmsUrl: string;
  layer: string;
  /** Genuine frame timestamps (ISO), oldest first. */
  frames: string[];
  latest: string | null;
  fetchedAt: string;
  attribution: string;
}

// ---------- Tasks ----------

export type TaskRecurrence =
  | { kind: "daily"; interval: number }
  | { kind: "weekly"; interval: number; weekdays: number[] } // 0 = Sunday
  | { kind: "monthly"; interval: number };

export type TaskKind = "task" | "chore";

export interface Task {
  id: string;
  kind: TaskKind;
  /** Chores: stars, or cents when the household currency is dollars. */
  reward: number;
  title: string;
  notes: string | null;
  dueDate: string | null;
  recurrence: TaskRecurrence | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /** For recurring tasks: when the most recent occurrence was completed. */
  lastCompletedAt: string | null;
}

export interface TaskInput {
  title: string;
  kind?: TaskKind;
  reward?: number;
  notes?: string | null;
  dueDate?: string | null;
  recurrence?: TaskRecurrence | null;
  assigneeId?: string | null;
}

// ---------- Calendar ----------

export interface EventTime {
  /** Timed events: RFC3339 instant with offset. */
  dateTime?: string;
  /** All-day events: 'YYYY-MM-DD' (end is exclusive, as in Google). */
  date?: string;
  timeZone?: string;
}

export interface CalendarEvent {
  id: string;
  calendarId: string;
  /** Title exactly as stored in Google. */
  title: string;
  /** Title for display, with recognized person tags ("Ethan: ", "#noah") removed. */
  displayTitle: string;
  start: EventTime;
  end: EventTime;
  allDay: boolean;
  location: string | null;
  description: string | null;
  color: string | null;
  recurringEventId: string | null;
  status: "confirmed" | "tentative" | "cancelled";
  canEdit: boolean;
  htmlLink: string | null;
  /** Household member ids involved (tapped in Kinmantle, or recognized from the title). */
  people: string[];
  /** Where `people` came from: tapped in Kinmantle, or name tags in the title/description. */
  peopleSource: "kinmantle" | "title" | null;
}

export type CalendarSource = "google" | "sample" | "none";

export interface CalendarFeed {
  source: CalendarSource;
  calendarName: string | null;
  events: CalendarEvent[];
  rangeStart: string;
  rangeEnd: string;
  fetchedAt: string;
  canEdit: boolean;
  note: string | null;
  /** Calendars shown, for color keys and choosing where a new event goes. */
  calendars?: CalendarInfo[];
  /** True when Google couldn't be reached and these are the last synced events. */
  stale?: boolean;
}

export interface CalendarInfo {
  id: string;
  name: string;
  color: string | null;
  canEdit: boolean;
  isDefault: boolean;
}

/** A calendar in the connected account, for the picker. */
export interface CalendarChoice {
  id: string;
  name: string;
  color: string | null;
  /** Google accessRole: owner | writer | reader | freeBusyReader. */
  accessRole: string;
  primary: boolean;
  selected: boolean;
  isDefault: boolean;
}

export interface CalendarStatus {
  /** False when this Kinmantle can't offer the Google connection at all. */
  available: boolean;
  connection: "none" | "active" | "needs_reconnect";
  /** Only shown to adults. */
  accountEmail: string | null;
  /** False when Google only granted read access. */
  canEdit: boolean;
  selectedCount: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

/** Event fields sent from the wall. Timed values are local wall-clock times in the household timezone. */
export interface EventInput {
  calendarId?: string;
  title?: string;
  allDay?: boolean;
  /** All-day: start date and exclusive end date ('YYYY-MM-DD'). */
  startDate?: string;
  endDate?: string;
  /** Timed: 'YYYY-MM-DDTHH:MM' in the household timezone. */
  startLocal?: string;
  endLocal?: string;
  location?: string;
  description?: string;
  memberIds?: string[];
  /** Recurring events: change only this occurrence, or every event in the series. */
  scope?: "one" | "series";
}

// ---------- Rewards & routines ----------

export interface RewardBalance {
  memberId: string;
  balance: number;
  earnedThisWeek: number;
}

export interface LedgerEntry {
  id: string;
  memberId: string;
  amount: number;
  kind: "chore" | "routine" | "spend" | "adjust";
  note: string | null;
  createdAt: string;
}

export interface RoutineStep {
  id: string;
  label: string;
  icon: string;
}

export interface Routine {
  id: string;
  name: string;
  period: "morning" | "evening" | "anytime";
  /** Empty = every child in the household. */
  memberIds: string[];
  reward: number;
  steps: RoutineStep[];
  /** Which days it's on; null = every day. */
  repeat: TaskRecurrence | null;
  /** The first day it's on: anchors "every 2 weeks" and "monthly". */
  startDate: string | null;
}

export interface RoutineDay {
  date: string;
  /** The routines that are on today. */
  routines: Routine[];
  /** How many routines the household has in all, today's or not. */
  total?: number;
  /** `${stepId}:${memberId}` keys checked today. */
  checked: string[];
  /** `${routineId}:${memberId}` routines already rewarded today. */
  rewarded: string[];
}

export interface TickerItem {
  kind: "news" | "onThisDay" | "quote" | "joke" | "word" | "fact";
  label: string;
  text: string;
  detail?: string;
  source?: string;
}

// ---------- AI ----------

export interface AiStatus {
  enabled: boolean;
  features: Record<string, boolean>;
  budgets: { perRequestUsd: number; dailyUsd: number; monthlyUsd: number };
  spend: { todayUsd: number; monthUsd: number; lifetimeUsd: number };
}

// ---------- Diagnostics ----------

export interface Diagnostics {
  build: string;
  serverTime: string;
  usageToday: { provider: string; requests: number; errors: number; rateLimited: number; limit: number | null }[];
  sources: {
    source: string;
    lastSuccessAt: string | null;
    lastErrorAt: string | null;
    lastError: string | null;
    consecutiveFailures: number;
  }[];
  ai: AiStatus;
}
